import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Save,
  Refresh,
  Add,
  Settings,
  Code,
  Description,
  Folder,
  FolderOpen,
  InsertDriveFile,
  ExpandMore,
  ExpandLess,
  Phone,
  Computer,
  CloudUpload,
  Download
} from '@mui/icons-material';
import MonacoEditor from '@monaco-editor/react';
import { websocketService } from '../../services/websocketService';
import { claudeCodeService } from '../../services/claudeCodeService';

// Add CSS for pulse animation
const pulseKeyframes = `
  @keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

// Inject styles
if (!document.querySelector('#code-dev-styles')) {
  const style = document.createElement('style');
  style.id = 'code-dev-styles';
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

const CodeDevelopmentPage = ({ 
  projectId, 
  initialTasks = [], 
  mode = 'development', // 'development' | 'file-editing' | 'method-processing'
  onCodeChange,
  onTaskComplete,
  mobileCompatible = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // State management
  const [tasks, setTasks] = useState(initialTasks);
  const [currentTask, setCurrentTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [workspaceStatus, setWorkspaceStatus] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['src']));
  const [viewMode, setViewMode] = useState(isMobile ? 'mobile' : 'desktop');
  const [mobileActivePanel, setMobileActivePanel] = useState('tasks'); // 'tasks' | 'editor' | 'files'
  
  // Real-time file operation states
  const [liveFileOperations, setLiveFileOperations] = useState(new Map());
  const [recentCodeBlocks, setRecentCodeBlocks] = useState([]);
  const [fileChangeIndicators, setFileChangeIndicators] = useState(new Map());
  
  // Dialog states
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [showTaskSettingsDialog, setShowTaskSettingsDialog] = useState(false);
  const [newTaskData, setNewTaskData] = useState({
    description: '',
    type: 'implementation',
    language: 'python',
    framework: ''
  });
  
  // Refs
  const editorRef = useRef(null);
  const wsRef = useRef(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Initialize WebSocket connection
  useEffect(() => {
    if (projectId) {
      initializeWebSocket();
      loadWorkspaceStatus();
    }
    
    return () => {
      // Clean up WebSocket connections
      if (websocketService.isConnected()) {
        websocketService.off('codeGeneration', handleCodeGenerationUpdate);
        websocketService.off('fileUpdate', handleFileUpdate);
        websocketService.off('taskProgress', handleTaskProgress);
        websocketService.off('workspaceUpdate', handleWorkspaceUpdate);
        websocketService.off('systemMessage', handleSystemMessage);
        websocketService.off('fileOperationLive', handleLiveFileOperation);
        websocketService.off('codeContentLive', handleLiveCodeContent);
        websocketService.disconnect();
      }
    };
  }, [projectId]);
  
  // Responsive layout detection
  useEffect(() => {
    setViewMode(isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop');
  }, [isMobile, isTablet]);
  
  const initializeWebSocket = useCallback(async () => {
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No authentication token found for WebSocket connection');
        showSnackbar('Authentication required for real-time updates', 'warning');
        return;
      }

      await websocketService.connect(projectId, token);
      
      // Subscribe to relevant channels using the new method names
      websocketService.on('codeGeneration', handleCodeGenerationUpdate);
      websocketService.on('fileUpdate', handleFileUpdate);
      websocketService.on('taskProgress', handleTaskProgress);
      websocketService.on('workspaceUpdate', handleWorkspaceUpdate);
      websocketService.on('systemMessage', handleSystemMessage);
      
      // Subscribe to new real-time events
      websocketService.on('fileOperationLive', handleLiveFileOperation);
      websocketService.on('codeContentLive', handleLiveCodeContent);
      
      // Subscribe to project-specific rooms
      websocketService.subscribeToProject(projectId);
      
      showSnackbar('Connected to real-time updates', 'success');
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      showSnackbar('Failed to connect to real-time updates', 'warning');
    }
  }, [projectId]);
  
  const loadWorkspaceStatus = async () => {
    try {
      const status = await claudeCodeService.getWorkspaceStatus(projectId);
      setWorkspaceStatus(status);
      if (status.files) {
        setFiles(status.files);
      }
    } catch (error) {
      console.error('Failed to load workspace status:', error);
      showSnackbar('Failed to load workspace status', 'error');
    }
  };
  
  const handleCodeGenerationUpdate = (data) => {
    if (data.type === 'code_generation_progress') {
      // Extract numeric progress value, default to current progress if not available
      const progressValue = typeof data.progress === 'number' ? data.progress : 
                           (data.progress?.percentage || data.messages_processed * 10 || 0);
      setProgress(Math.min(progressValue, 100));
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: data.progress?.content || data.message || 'Code generation update',
        type: 'info'
      }]);
    } else if (data.type === 'code_generation_complete') {
      setIsGenerating(false);
      setProgress(100);
      showSnackbar('Code generation completed', 'success');
      loadWorkspaceStatus(); // Refresh files
      if (onTaskComplete) {
        onTaskComplete(data);
      }
    }
  };
  
  const handleFileUpdate = (data) => {
    if (data.operation === 'created' || data.operation === 'updated') {
      // Update current file if it's the one being modified
      if (currentFile && currentFile.path === data.file_path) {
        setFileContent(data.content);
        if (editorRef.current) {
          editorRef.current.setValue(data.content);
        }
      }
      
      // Refresh file list
      loadWorkspaceStatus();
      
      showSnackbar(`File ${data.operation}: ${data.file_path}`, 'info');
    }
  };
  
  const handleTaskProgress = (data) => {
    setTasks(prev => prev.map(task => 
      task.id === data.task_id 
        ? { ...task, status: data.progress?.status, progress: data.progress }
        : task
    ));
    
    if (data.progress?.message) {
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: data.progress.message,
        type: data.progress.status === 'failed' ? 'error' : 'info'
      }]);
    }
  };
  
  const handleWorkspaceUpdate = (data) => {
    setWorkspaceStatus(data.workspace);
    if (data.workspace.files) {
      setFiles(data.workspace.files);
    }
  };
  
  const handleSystemMessage = (data) => {
    showSnackbar(data.message, data.level || 'info');
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message: data.message,
      type: data.level || 'info'
    }]);
  };
  
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleLiveFileOperation = (data) => {
    if (data.type === 'file_operation_live') {
      // Update live file operations map
      setLiveFileOperations(prev => {
        const newMap = new Map(prev);
        newMap.set(data.file_path, {
          operation: data.operation,
          context: data.context,
          timestamp: data.timestamp
        });
        return newMap;
      });
      
      // Add visual indicator for file changes
      setFileChangeIndicators(prev => {
        const newMap = new Map(prev);
        newMap.set(data.file_path, {
          operation: data.operation,
          timestamp: Date.now()
        });
        return newMap;
      });
      
      // Auto-clear indicator after 5 seconds
      setTimeout(() => {
        setFileChangeIndicators(prev => {
          const newMap = new Map(prev);
          newMap.delete(data.file_path);
          return newMap;
        });
      }, 5000);
      
      // Add to logs
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `${data.operation} ${data.file_path}: ${data.context.substring(0, 100)}...`,
        type: 'file_operation'
      }]);
      
      showSnackbar(`${data.operation} ${data.file_path}`, 'info');
    }
  };
  
  const handleLiveCodeContent = (data) => {
    if (data.type === 'code_content_live') {
      // Add code block to recent code blocks
      setRecentCodeBlocks(prev => [
        {
          id: Date.now(),
          language: data.language,
          code: data.code,
          timestamp: data.timestamp,
          type: data.code_type
        },
        ...prev.slice(0, 9) // Keep only last 10 code blocks
      ]);
      
      // If currently viewing a file of the same language, optionally update
      if (currentFile && getLanguageFromExtension(currentFile.path) === data.language) {
        // Show indicator that new code is available
        showSnackbar(`New ${data.language} code generated`, 'info');
      }
      
      // Add to logs
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Generated ${data.language} code (${data.code.length} characters)`,
        type: 'code_generation'
      }]);
    }
  };
  
  const startCodeGeneration = async (task) => {
    if (isGenerating) {
      showSnackbar('Code generation already in progress', 'warning');
      return;
    }
    
    try {
      setIsGenerating(true);
      setCurrentTask(task);
      setProgress(0);
      setLogs([]);
      
      // Start code generation via API
      await claudeCodeService.generateCodeFromTask(projectId, {
        task_description: task.description,
        task_type: task.type || 'implementation',
        language: task.language || 'python',
        framework: task.framework,
        stream_enabled: true
      });
      
      showSnackbar('Code generation started', 'info');
    } catch (error) {
      console.error('Failed to start code generation:', error);
      setIsGenerating(false);
      showSnackbar('Failed to start code generation', 'error');
    }
  };
  
  const stopCodeGeneration = () => {
    setIsGenerating(false);
    setCurrentTask(null);
    showSnackbar('Code generation stopped', 'info');
  };
  
  const openFile = async (file) => {
    try {
      const content = await claudeCodeService.readFile(projectId, file.path);
      setCurrentFile(file);
      setFileContent(content.content || '');
      
      // Switch to editor view on mobile
      if (viewMode === 'mobile') {
        setMobileActivePanel('editor');
      }
    } catch (error) {
      console.error('Failed to open file:', error);
      showSnackbar('Failed to open file', 'error');
    }
  };
  
  const saveFile = async () => {
    if (!currentFile) return;
    
    try {
      await claudeCodeService.writeFile(projectId, currentFile.path, fileContent);
      showSnackbar('File saved successfully', 'success');
      
      if (onCodeChange) {
        onCodeChange(currentFile.path, fileContent);
      }
    } catch (error) {
      console.error('Failed to save file:', error);
      showSnackbar('Failed to save file', 'error');
    }
  };
  
  const addNewTask = () => {
    const newTask = {
      id: Date.now().toString(),
      description: newTaskData.description,
      type: newTaskData.type,
      language: newTaskData.language,
      framework: newTaskData.framework,
      status: 'pending'
    };
    
    setTasks(prev => [...prev, newTask]);
    setShowNewTaskDialog(false);
    setNewTaskData({ description: '', type: 'implementation', language: 'python', framework: '' });
    showSnackbar('Task added successfully', 'success');
  };
  
  const createWorkspace = async () => {
    try {
      await claudeCodeService.createWorkspace(projectId, { project_name: `Project ${projectId}` });
      await loadWorkspaceStatus();
      showSnackbar('Workspace created successfully', 'success');
    } catch (error) {
      console.error('Failed to create workspace:', error);
      showSnackbar('Failed to create workspace', 'error');
    }
  };
  
  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };
  
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconMap = {
      'js': '📄',
      'ts': '📘',
      'py': '🐍',
      'html': '🌐',
      'css': '🎨',
      'json': '⚙️',
      'md': '📝',
      'txt': '📄'
    };
    return iconMap[ext] || '📄';
  };
  
  const renderFileTree = (files) => {
    const tree = {};
    
    files.forEach(file => {
      const parts = file.path.split('/');
      let current = tree;
      
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // It's a file
          current[part] = { type: 'file', data: file };
        } else {
          // It's a folder
          if (!current[part]) {
            current[part] = { type: 'folder', children: {} };
          }
          current = current[part].children;
        }
      });
    });
    
    const renderTreeNode = (name, node, path = '') => {
      const fullPath = path ? `${path}/${name}` : name;
      
      if (node.type === 'file') {
        const fileIndicator = fileChangeIndicators.get(node.data.path);
        const liveOperation = liveFileOperations.get(node.data.path);
        
        return (
          <ListItem
            key={fullPath}
            button
            onClick={() => openFile(node.data)}
            selected={currentFile?.path === node.data.path}
            sx={{ 
              pl: 4 + (fullPath.split('/').length - 1) * 2,
              backgroundColor: fileIndicator ? (
                fileIndicator.operation === 'creating' ? 'rgba(76, 175, 80, 0.1)' :
                fileIndicator.operation === 'editing' ? 'rgba(255, 193, 7, 0.1)' :
                'rgba(33, 150, 243, 0.1)'
              ) : 'inherit',
              borderLeft: fileIndicator ? (
                fileIndicator.operation === 'creating' ? '3px solid #4CAF50' :
                fileIndicator.operation === 'editing' ? '3px solid #FFC107' :
                '3px solid #2196F3'
              ) : 'none'
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Box position="relative">
                <span style={{ fontSize: '16px' }}>{getFileIcon(name)}</span>
                {fileIndicator && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 
                        fileIndicator.operation === 'creating' ? '#4CAF50' :
                        fileIndicator.operation === 'editing' ? '#FFC107' :
                        '#2196F3',
                      animation: 'pulse 1.5s infinite'
                    }}
                  />
                )}
              </Box>
            </ListItemIcon>
            <ListItemText 
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <span>{name}</span>
                  {liveOperation && (
                    <Chip 
                      label={liveOperation.operation}
                      size="small"
                      variant="outlined"
                      color={
                        liveOperation.operation === 'creating' ? 'success' :
                        liveOperation.operation === 'editing' ? 'warning' :
                        'primary'
                      }
                      sx={{ fontSize: '0.6rem', height: 18 }}
                    />
                  )}
                </Box>
              }
              secondary={`${(node.data.size / 1024).toFixed(1)} KB`}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItem>
        );
      } else {
        const isExpanded = expandedFolders.has(fullPath);
        return (
          <div key={fullPath}>
            <ListItem
              button
              onClick={() => toggleFolder(fullPath)}
              sx={{ pl: 2 + (fullPath.split('/').length - 1) * 2 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isExpanded ? <FolderOpen /> : <Folder />}
              </ListItemIcon>
              <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {Object.entries(node.children).map(([childName, childNode]) =>
                renderTreeNode(childName, childNode, fullPath)
              )}
            </Collapse>
          </div>
        );
      }
    };
    
    return (
      <List dense>
        {Object.entries(tree).map(([name, node]) => renderTreeNode(name, node))}
      </List>
    );
  };
  
  const renderTaskPanel = () => (
    <Paper elevation={1} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box p={2} borderBottom={1} borderColor="divider" display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">
          Development Tasks
        </Typography>
        <IconButton size="small" onClick={() => setShowNewTaskDialog(true)}>
          <Add />
        </IconButton>
      </Box>
      
      <Box flexGrow={1} overflow="auto">
        {tasks.length === 0 ? (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              No tasks yet. Add a task to get started.
            </Typography>
          </Box>
        ) : (
          <List>
            {tasks.map((task, index) => (
              <ListItem key={task.id} divider={index < tasks.length - 1}>
                <Box width="100%">
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" noWrap>
                      {task.description}
                    </Typography>
                    <Chip 
                      label={task.status || 'pending'} 
                      size="small"
                      color={task.status === 'completed' ? 'success' : task.status === 'failed' ? 'error' : 'default'}
                    />
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip label={task.language} size="small" variant="outlined" />
                    <Chip label={task.type} size="small" variant="outlined" />
                    {task.framework && <Chip label={task.framework} size="small" variant="outlined" />}
                  </Box>
                  
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={isGenerating && currentTask?.id === task.id ? <Stop /> : <PlayArrow />}
                      onClick={() => isGenerating && currentTask?.id === task.id ? stopCodeGeneration() : startCodeGeneration(task)}
                      disabled={isGenerating && currentTask?.id !== task.id}
                    >
                      {isGenerating && currentTask?.id === task.id ? 'Stop' : 'Run'}
                    </Button>
                  </Box>
                  
                  {isGenerating && currentTask?.id === task.id && (
                    <Box mt={1}>
                      <LinearProgress variant="determinate" value={typeof progress === 'number' ? progress : 0} />
                      <Typography variant="caption" color="textSecondary">
                        {(typeof progress === 'number' ? progress : 0).toFixed(0)}% complete
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      
      {/* Recent Code Blocks Section */}
      {recentCodeBlocks.length > 0 && (
        <>
          <Divider />
          <Box p={2} maxHeight={150} overflow="auto" bgcolor="purple.50">
            <Typography variant="subtitle2" gutterBottom color="secondary">
              Recent Code Generated
            </Typography>
            {recentCodeBlocks.slice(0, 3).map((codeBlock) => (
              <Box key={codeBlock.id} mb={1} p={1} bgcolor="white" borderRadius={1} border="1px solid #e0e0e0">
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Chip label={codeBlock.language} size="small" color="secondary" />
                  <Typography variant="caption" color="textSecondary">
                    {new Date(codeBlock.timestamp * 1000).toLocaleTimeString()}
                  </Typography>
                </Box>
                <Box 
                  component="pre" 
                  sx={{ 
                    fontSize: '0.7rem', 
                    fontFamily: 'monospace', 
                    margin: 0, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.secondary'
                  }}
                >
                  {codeBlock.code.substring(0, 100)}...
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Progress and Logs Section */}
      {(isGenerating || logs.length > 0) && (
        <>
          <Divider />
          <Box p={2} maxHeight={200} overflow="auto" bgcolor="grey.50">
            <Typography variant="subtitle2" gutterBottom>
              Generation Logs
            </Typography>
            {logs.map((log, index) => (
              <Box key={index} mb={0.5}>
                <Typography 
                  variant="caption" 
                  display="block"
                  color={
                    log.type === 'error' ? 'error' : 
                    log.type === 'file_operation' ? 'primary' :
                    log.type === 'code_generation' ? 'secondary' :
                    'textSecondary'
                  }
                  sx={{
                    fontFamily: log.type === 'code_generation' ? 'monospace' : 'inherit',
                    backgroundColor: 
                      log.type === 'file_operation' ? 'rgba(33, 150, 243, 0.05)' :
                      log.type === 'code_generation' ? 'rgba(156, 39, 176, 0.05)' :
                      'transparent',
                    padding: log.type !== 'info' ? '2px 4px' : '0',
                    borderRadius: '2px'
                  }}
                >
                  <Box component="span" fontWeight="bold">
                    [{log.timestamp}]
                  </Box>
                  {log.type === 'file_operation' && <Chip label="FILE" size="small" color="primary" sx={{ fontSize: '0.6rem', height: 16, mx: 0.5 }} />}
                  {log.type === 'code_generation' && <Chip label="CODE" size="small" color="secondary" sx={{ fontSize: '0.6rem', height: 16, mx: 0.5 }} />}
                  {log.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
  
  const renderFileExplorer = () => (
    <Paper elevation={1} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box p={2} borderBottom={1} borderColor="divider" display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">
          Project Files
        </Typography>
        <IconButton size="small" onClick={loadWorkspaceStatus}>
          <Refresh />
        </IconButton>
      </Box>
      
      <Box flexGrow={1} overflow="auto">
        {!workspaceStatus?.workspace_exists ? (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="textSecondary" gutterBottom>
              No workspace found
            </Typography>
            <Button variant="contained" onClick={createWorkspace}>
              Create Workspace
            </Button>
          </Box>
        ) : files.length === 0 ? (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              No files yet. Generate some code to see files here.
            </Typography>
          </Box>
        ) : (
          renderFileTree(files)
        )}
      </Box>
    </Paper>
  );
  
  const renderCodeEditor = () => (
    <Paper elevation={1} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box p={2} borderBottom={1} borderColor="divider" display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" noWrap>
          {currentFile ? currentFile.path : 'Code Editor'}
        </Typography>
        <Box display="flex" gap={1}>
          {currentFile && (
            <Button size="small" startIcon={<Save />} onClick={saveFile}>
              Save
            </Button>
          )}
          <IconButton size="small">
            <Settings />
          </IconButton>
        </Box>
      </Box>
      
      <Box flexGrow={1} minHeight={0}>
        {currentFile ? (
          <MonacoEditor
            height="100%"
            language={getLanguageFromExtension(currentFile.path)}
            value={fileContent}
            onChange={(value) => setFileContent(value || '')}
            onMount={(editor) => { editorRef.current = editor; }}
            options={{
              minimap: { enabled: !isMobile },
              fontSize: isMobile ? 12 : 14,
              lineNumbers: 'on',
              wordWrap: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              folding: true,
              selectOnLineNumbers: true,
              matchBrackets: 'always'
            }}
            theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'vs-light'}
          />
        ) : (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            height="100%" 
            bgcolor="grey.50"
          >
            <Box textAlign="center">
              <Code sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No file selected
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Select a file from the explorer or generate new code
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
  
  const getLanguageFromExtension = (filePath) => {
    const ext = filePath.split('.').pop()?.toLowerCase();
    const languageMap = {
      'js': 'javascript',
      'ts': 'typescript',
      'py': 'python',
      'html': 'html',
      'css': 'css',
      'json': 'json',
      'md': 'markdown',
      'txt': 'plaintext'
    };
    return languageMap[ext] || 'plaintext';
  };
  
  // Mobile layout
  if (viewMode === 'mobile') {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile Header */}
        <Paper elevation={1} square>
          <Tabs 
            value={mobileActivePanel} 
            onChange={(e, newValue) => setMobileActivePanel(newValue)}
            variant="fullWidth"
          >
            <Tab icon={<Description />} label="Tasks" value="tasks" />
            <Tab icon={<Code />} label="Editor" value="editor" />
            <Tab icon={<Folder />} label="Files" value="files" />
          </Tabs>
        </Paper>
        
        {/* Mobile Content */}
        <Box flexGrow={1} overflow="hidden">
          {mobileActivePanel === 'tasks' && renderTaskPanel()}
          {mobileActivePanel === 'editor' && renderCodeEditor()}
          {mobileActivePanel === 'files' && renderFileExplorer()}
        </Box>
        
        {/* Mobile Actions */}
        {mobileCompatible && (
          <Paper elevation={1} square>
            <Box p={1} display="flex" justifyContent="space-around">
              <IconButton size="small" color="primary">
                <Phone />
              </IconButton>
              <IconButton size="small" onClick={saveFile} disabled={!currentFile}>
                <CloudUpload />
              </IconButton>
              <IconButton size="small" onClick={loadWorkspaceStatus}>
                <Refresh />
              </IconButton>
              <IconButton size="small">
                <Download />
              </IconButton>
            </Box>
          </Paper>
        )}
      </Box>
    );
  }
  
  // Desktop/Tablet layout
  return (
    <Box sx={{ height: '100vh', overflow: 'hidden' }}>
      <Grid container spacing={1} sx={{ height: '100%' }}>
        {/* Left Panel - Tasks */}
        <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
          {renderTaskPanel()}
        </Grid>
        
        {/* Center Panel - Code Editor */}
        <Grid item xs={12} md={5} lg={6} sx={{ height: '100%' }}>
          {renderCodeEditor()}
        </Grid>
        
        {/* Right Panel - File Explorer */}
        <Grid item xs={12} md={3} lg={3} sx={{ height: '100%' }}>
          {renderFileExplorer()}
        </Grid>
      </Grid>
      
      {/* New Task Dialog */}
      <Dialog open={showNewTaskDialog} onClose={() => setShowNewTaskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Task Description"
              multiline
              rows={3}
              value={newTaskData.description}
              onChange={(e) => setNewTaskData(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={newTaskData.type}
                onChange={(e) => setNewTaskData(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="implementation">Implementation</MenuItem>
                <MenuItem value="testing">Testing</MenuItem>
                <MenuItem value="documentation">Documentation</MenuItem>
                <MenuItem value="refactoring">Refactoring</MenuItem>
                <MenuItem value="debugging">Debugging</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={newTaskData.language}
                onChange={(e) => setNewTaskData(prev => ({ ...prev, language: e.target.value }))}
              >
                <MenuItem value="python">Python</MenuItem>
                <MenuItem value="javascript">JavaScript</MenuItem>
                <MenuItem value="typescript">TypeScript</MenuItem>
                <MenuItem value="html">HTML</MenuItem>
                <MenuItem value="css">CSS</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Framework (Optional)"
              value={newTaskData.framework}
              onChange={(e) => setNewTaskData(prev => ({ ...prev, framework: e.target.value }))}
              placeholder="e.g., React, FastAPI, Django"
              fullWidth
            />
            
            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button onClick={() => setShowNewTaskDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={addNewTask}
                disabled={!newTaskData.description.trim()}
              >
                Add Task
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CodeDevelopmentPage;